/* eslint-disable react/forbid-dom-props */
import { useRef, useState, useEffect } from 'react';

import { Placement } from '@floating-ui/react';
import classNames from 'classnames';

import DotsMenu from '@travauxlib/shared/src/components/DesignSystem/assets/DotsMenu.svg?react';
import {
  ActionList,
  ActionListProps,
  ItemType,
} from '@travauxlib/shared/src/components/DesignSystem/components/ActionList';

import { RowAction } from './types';
import { columnClassName, verticalLastColShadowClassName } from './utils';

import { IconButton } from '../Buttons/IconButton';

type Props<T> = {
  rowActions: RowAction[];
  fixedColumn?: 'first' | 'last';
  rowActionsPosition?: Placement;
  actionListProps?: Partial<ActionListProps<T>>;
  height?: number;
};

export const ActionsColumn = <T extends object>({
  rowActions,
  fixedColumn,
  rowActionsPosition,
  actionListProps,
  height,
}: Props<T>): React.ReactElement => {
  const ref = useRef(null);
  const [width, setWidth] = useState();

  useEffect(() => {
    setWidth((ref?.current as any)?.getBoundingClientRect().width);
  }, [ref]);

  // Since we are now using absolute positioning, we have to add a fake element to make up for
  // the lost column
  return (
    <>
      {fixedColumn === 'last' && (
        <td className="invisible" style={{ width: `${width}px`, maxWidth: `${width}px` }} />
      )}
      <td
        ref={ref}
        className={classNames(
          columnClassName,
          fixedColumn === 'last' && verticalLastColShadowClassName,
        )}
        style={
          height && fixedColumn === 'last'
            ? {
                height: `${height}px`,
              }
            : undefined
        }
      >
        <ActionList
          small
          fixedFloating
          position={rowActionsPosition}
          trigger={({ toggle, referenceProps }) => (
            <IconButton
              onClick={toggle}
              aria-label="Actions"
              data-testid="open-actions-menu"
              className="p-0! rounded-full! hover:action-bg-neutral-hover! hover:text-neutral-strong! relative"
              {...referenceProps}
            >
              <DotsMenu />
            </IconButton>
          )}
          items={rowActions.map(({ action, ...rest }) => ({
            ...rest,
            type: ItemType.Action,
            onClick: action,
          }))}
          {...actionListProps}
        />
      </td>
    </>
  );
};
